import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { getInitials } from '../../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import {
  BarChart3,
  Users,
  FolderKanban,
  Building2,
  CalendarDays,
  Clock,
  TrendingUp,
  TrendingDown,
  Download,
  FileText,
  Table2,
  ChevronDown,
  ChevronRight,
  X,
  Check,
  Minus,
  AlertTriangle,
  Target,
  Filter,
  Plus,
  Trash2,
  Mail,
  Calendar as CalendarIcon,
  ArrowUpRight,
  ArrowDownRight,
  PieChart as PieChartIcon,
  Mouse,
  Keyboard,
  Activity
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ActivityBar, ActivityBadge, formatTrackedTime } from '../../components/activity/ActivityBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'custom', label: 'Custom Range' }
];

export default function AdminReports() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Main state
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('week');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    department: 'all',
    project: 'all',
    client: 'all',
    employee: 'all'
  });
  const [filterOptions, setFilterOptions] = useState({
    departments: [],
    projects: [],
    clients: [],
    employees: []
  });
  const [activeFilters, setActiveFilters] = useState([]);
  
  // Data state
  const [summaryData, setSummaryData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [overtimeData, setOvertimeData] = useState(null);
  const [activityData, setActivityData] = useState(null);
  
  // Drill-down state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownModalOpen, setDrillDownModalOpen] = useState(false);
  
  // Scheduled reports
  const [scheduledReports, setScheduledReports] = useState([]);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    report_type: 'summary',
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    recipients: [],
    email_subject: ''
  });

  // Access check
  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('You do not have access to this page');
    }
  }, [user, navigate]);

  // Build query params
  const getQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    params.append('range', dateRange);
    if (dateRange === 'custom' && customStart) params.append('start_date', format(customStart, 'yyyy-MM-dd'));
    if (dateRange === 'custom' && customEnd) params.append('end_date', format(customEnd, 'yyyy-MM-dd'));
    if (filters.department !== 'all') params.append('department', filters.department);
    if (filters.project !== 'all') params.append('project_id', filters.project);
    if (filters.client !== 'all') params.append('client', filters.client);
    if (filters.employee !== 'all') params.append('employee_id', filters.employee);
    return params.toString();
  }, [dateRange, customStart, customEnd, filters]);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/filters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setFilterOptions({
        departments: data.departments || [],
        projects: data.projects || [],
        clients: data.clients || [],
        employees: data.employees || []
      });
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, [token]);

  // Fetch summary data
  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/summary?${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSummaryData(data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  }, [token, getQueryParams]);

  // Fetch employee data
  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/by-employee?${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEmployeeData(data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, [token, getQueryParams]);

  // Fetch project data
  const fetchProjects = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/by-project?${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setProjectData(data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, [token, getQueryParams]);

  // Fetch client data
  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/by-client?${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setClientData(data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, [token, getQueryParams]);

  // Fetch attendance data
  const fetchAttendance = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/attendance?${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAttendanceData(data);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    }
  }, [token, getQueryParams]);

  // Fetch overtime data
  const fetchOvertime = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/overtime?${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setOvertimeData(data);
    } catch (error) {
      console.error('Failed to fetch overtime:', error);
    }
  }, [token, getQueryParams]);

  // Fetch activity data
  const fetchActivity = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      // Derive start_date/end_date from dateRange
      const now = new Date();
      let start, end;
      switch (dateRange) {
        case 'today':
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
          break;
        case 'week': {
          const d = now.getDay();
          const diff = d === 0 ? 6 : d - 1;
          start = new Date(now);
          start.setDate(now.getDate() - diff);
          start.setHours(0, 0, 0, 0);
          end = new Date(start);
          end.setDate(start.getDate() + 6);
          end.setHours(23, 59, 59, 999);
          break;
        }
        case 'month':
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
          break;
        case 'lastWeek': {
          const lwd = now.getDay();
          const lwDiff = lwd === 0 ? 6 : lwd - 1;
          start = new Date(now);
          start.setDate(now.getDate() - lwDiff - 7);
          start.setHours(0, 0, 0, 0);
          end = new Date(start);
          end.setDate(start.getDate() + 6);
          end.setHours(23, 59, 59, 999);
          break;
        }
        case 'lastMonth':
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
          break;
        case 'custom':
          start = customStart ? new Date(customStart) : new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          end = customEnd ? new Date(customEnd) : now;
          end.setHours(23, 59, 59, 999);
          break;
        default:
          start = new Date(now);
          start.setDate(now.getDate() - (now.getDay() === 0 ? 6 : now.getDay() - 1));
          start.setHours(0, 0, 0, 0);
          end = new Date(start);
          end.setDate(start.getDate() + 6);
          end.setHours(23, 59, 59, 999);
      }
      const res = await fetch(`${API_URL}/api/activity/team-summary?start_date=${start.toISOString()}&end_date=${end.toISOString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setActivityData(data);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  }, [token, dateRange, customStart, customEnd]);

  // Initial load
  useEffect(() => {
    if (token) {
      fetchFilterOptions();
    }
  }, [token, fetchFilterOptions]);

  // Fetch data based on active tab
  useEffect(() => {
    if (!token) return;
    
    setLoading(true);
    const fetchData = async () => {
      switch (activeTab) {
        case 'summary':
          await fetchSummary();
          break;
        case 'employees':
          await fetchEmployees();
          break;
        case 'projects':
          await fetchProjects();
          break;
        case 'clients':
          await fetchClients();
          break;
        case 'attendance':
          await fetchAttendance();
          break;
        case 'overtime':
          await fetchOvertime();
          break;
        case 'activity':
          await fetchActivity();
          break;
      }
      setLoading(false);
    };
    fetchData();
  }, [token, activeTab, dateRange, customStart, customEnd, filters, fetchSummary, fetchEmployees, fetchProjects, fetchClients, fetchAttendance, fetchOvertime, fetchActivity]);

  // Drill-down handlers
  const handleEmployeeDrillDown = async (employee) => {
    setSelectedEmployee(employee);
    setSelectedProject(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/employee/${employee.id}?${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDrillDownData(data);
      setDrillDownModalOpen(true);
    } catch (error) {
      toast.error('Failed to fetch employee details');
    }
  };

  const handleProjectDrillDown = async (project) => {
    setSelectedProject(project);
    setSelectedEmployee(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/project/${project.id}?${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setDrillDownData(data);
      setDrillDownModalOpen(true);
    } catch (error) {
      toast.error('Failed to fetch project details');
    }
  };

  // Export handlers
  const handleExportCSV = async (reportType) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/export/csv?report_type=${reportType}&${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${reportType}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success('CSV downloaded');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPDF = async (reportType) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/export/pdf?report_type=${reportType}&${getQueryParams()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${reportType}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  // Filter management
  const addFilter = (type, value, label) => {
    if (value === 'all') return;
    setFilters(prev => ({ ...prev, [type]: value }));
    setActiveFilters(prev => {
      const existing = prev.find(f => f.type === type);
      if (existing) {
        return prev.map(f => f.type === type ? { type, value, label } : f);
      }
      return [...prev, { type, value, label }];
    });
  };

  const removeFilter = (type) => {
    setFilters(prev => ({ ...prev, [type]: 'all' }));
    setActiveFilters(prev => prev.filter(f => f.type !== type));
  };

  // Helpers
  const formatHours = (hours) => {
    if (hours === null || hours === undefined) return '-';
    return `${hours.toFixed(1)}h`;
  };

  const getHealthBadge = (health) => {
    switch (health) {
      case 'green': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
      case 'yellow': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
      case 'red': return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-700';
    }
  };

  const getTimesheetBadge = (status) => {
    switch (status) {
      case 'approved': return 'bg-emerald-100 text-emerald-700';
      case 'submitted': return 'bg-blue-100 text-blue-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-700';
    }
  };

  const TrendIndicator = ({ value, showValue = true }) => {
    if (value === 0 || value === null || value === undefined) return null;
    const isPositive = value > 0;
    return (
      <span className={`inline-flex items-center text-sm ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
        {showValue && <span>{Math.abs(value).toFixed(1)}%</span>}
      </span>
    );
  };

  if (loading && !summaryData && !employeeData) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {t('reports.title')}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {t('admin.reportsSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-48">
                <div className="space-y-1">
                  <Button variant="ghost" className="w-full justify-start" onClick={() => handleExportCSV(activeTab)} data-testid="export-csv-btn">
                    <Table2 className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button variant="ghost" className="w-full justify-start" onClick={() => handleExportPDF(activeTab)} data-testid="export-pdf-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Date Range & Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Date Range */}
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium whitespace-nowrap">Period:</Label>
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger className="w-[150px]" data-testid="date-range-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {dateRange === 'custom' && (
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {customStart ? format(customStart, 'MMM d') : 'Start'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={customStart} onSelect={setCustomStart} />
                      </PopoverContent>
                    </Popover>
                    <span className="text-slate-400">to</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {customEnd ? format(customEnd, 'MMM d') : 'End'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex-wrap">
            <TabsTrigger value="summary" data-testid="tab-summary">
              <BarChart3 className="w-4 h-4 mr-2" />
              {t('reports.summary')}
            </TabsTrigger>
            <TabsTrigger value="employees" data-testid="tab-employees">
              <Users className="w-4 h-4 mr-2" />
              {t('reports.byEmployee')}
            </TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">
              <FolderKanban className="w-4 h-4 mr-2" />
              {t('reports.byProject')}
            </TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients">
              <Building2 className="w-4 h-4 mr-2" />
              {t('reports.byClient')}
            </TabsTrigger>
            <TabsTrigger value="attendance" data-testid="tab-attendance">
              <CalendarDays className="w-4 h-4 mr-2" />
              {t('reports.attendance')}
            </TabsTrigger>
            <TabsTrigger value="overtime" data-testid="tab-overtime">
              <AlertTriangle className="w-4 h-4 mr-2" />
              {t('reports.overtime')}
            </TabsTrigger>
          </TabsList>

          {/* SUMMARY TAB */}
          <TabsContent value="summary" className="space-y-6">
            {summaryData && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="total-hours">
                            {formatHours(summaryData.total_hours)}
                          </p>
                          <p className="text-sm text-slate-500">Total Hours</p>
                        </div>
                        {showComparison && <TrendIndicator value={summaryData.comparison?.hours_change_percent} />}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="active-employees">
                        {summaryData.active_employees}
                      </p>
                      <p className="text-sm text-slate-500">Active Employees</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {summaryData.avg_hours_per_employee_per_day}h
                      </p>
                      <p className="text-sm text-slate-500">Avg Hours/Day/Employee</p>
                    </CardContent>
                  </Card>

                  <Card className="border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-900/30/50 dark:bg-blue-900/5">
                    <CardContent className="p-4">
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-testid="productivity-score">
                        {summaryData.productivity_score}%
                      </p>
                      <p className="text-sm text-slate-500">Productivity Score</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Daily Trend */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Daily Hours Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        {(!summaryData.daily_breakdown || summaryData.daily_breakdown.length === 0 || summaryData.daily_breakdown.every(d => !d.hours || d.hours === 0)) ? (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <TrendingUp className="w-12 h-12 mb-3 text-slate-300" />
                            <p className="text-sm font-medium">No data for this period</p>
                            <p className="text-xs mt-1">Time entries will appear here once tracked</p>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={summaryData.daily_breakdown}>
                              <XAxis dataKey="date" tickFormatter={(d) => format(parseISO(d), 'MMM d')} fontSize={12} />
                              <YAxis fontSize={12} />
                              <Tooltip 
                                formatter={(value) => [`${value.toFixed(1)}h`, 'Hours']}
                                labelFormatter={(d) => format(parseISO(d), 'EEEE, MMM d')}
                              />
                              <Line type="monotone" dataKey="hours" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }} />
                            </LineChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Project Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Hours by Project</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        {(!summaryData.project_breakdown || summaryData.project_breakdown.length === 0) ? (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <PieChartIcon className="w-12 h-12 mb-3 text-slate-300" />
                            <p className="text-sm font-medium">No data for this period</p>
                            <p className="text-xs mt-1">Project hours will appear here once tracked</p>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={summaryData.project_breakdown}
                                dataKey="hours"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                labelLine={false}
                              >
                                {summaryData.project_breakdown.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => `${value.toFixed(1)}h`} />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* BY EMPLOYEE TAB */}
          <TabsContent value="employees" className="space-y-4">
            {employeeData && (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="employee-table">
                      <thead className="bg-slate-50 dark:bg-slate-800/50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Employee</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Total Hours</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Daily Avg</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Days Tracked</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Timesheet</th>
                          {showComparison && <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Trend</th>}
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {employeeData.employees.map(emp => (
                          <tr 
                            key={emp.id} 
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                            onClick={() => handleEmployeeDrillDown(emp)}
                            data-testid={`employee-row-${emp.id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-9 w-9">
                                  {emp.profile_photo && <AvatarImage src={emp.profile_photo} />}
                                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm">
                                    {getInitials(emp.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-slate-900 dark:text-white">{emp.name}</p>
                                  <p className="text-xs text-slate-500">{emp.department || <span className="italic">Not assigned</span>}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-medium">{formatHours(emp.total_hours)}</td>
                            <td className="px-4 py-3 text-right text-slate-600">{formatHours(emp.daily_average)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={emp.days_tracked < emp.working_days ? 'text-amber-600' : 'text-slate-600'}>
                                {emp.days_tracked}/{emp.working_days}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={getTimesheetBadge(emp.timesheet_status)}>
                                {emp.timesheet_status}
                              </Badge>
                            </td>
                            {showComparison && (
                              <td className="px-4 py-3 text-right">
                                <TrendIndicator value={emp.trend_percent} />
                              </td>
                            )}
                            <td className="px-4 py-3 text-right">
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* BY PROJECT TAB */}
          <TabsContent value="projects" className="space-y-4">
            {projectData && (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="project-table">
                      <thead className="bg-slate-50 dark:bg-slate-800/50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Project</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Client</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Hours</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Team</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Progress</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Health</th>
                          {showComparison && <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Trend</th>}
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {projectData.projects.map(proj => (
                          <tr 
                            key={proj.id} 
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                            onClick={() => handleProjectDrillDown(proj)}
                            data-testid={`project-row-${proj.id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: proj.color }} />
                                <span className="font-medium text-slate-900 dark:text-white">{proj.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-600">{proj.client || '-'}</td>
                            <td className="px-4 py-3 text-right font-medium">{formatHours(proj.total_hours)}</td>
                            <td className="px-4 py-3 text-center">{proj.employees_count}</td>
                            <td className="px-4 py-3">
                              {proj.progress_percent !== null ? (
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full ${proj.health === 'red' ? 'bg-red-50 dark:bg-red-900/30' : proj.health === 'yellow' ? 'bg-amber-50 dark:bg-amber-900/30' : 'bg-emerald-50 dark:bg-emerald-900/30'}`}
                                      style={{ width: `${Math.min(proj.progress_percent, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-slate-500 w-10">{proj.progress_percent}%</span>
                                </div>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={getHealthBadge(proj.health)}>
                                {proj.health === 'green' ? 'On Track' : proj.health === 'yellow' ? 'At Risk' : 'Over Budget'}
                              </Badge>
                            </td>
                            {showComparison && (
                              <td className="px-4 py-3 text-right">
                                <TrendIndicator value={proj.trend_percent} />
                              </td>
                            )}
                            <td className="px-4 py-3 text-right">
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* BY CLIENT TAB */}
          <TabsContent value="clients" className="space-y-4">
            {clientData && (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="client-table">
                      <thead className="bg-slate-50 dark:bg-slate-800/50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Client</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Total Hours</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Projects</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Team Members</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Entries</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {clientData.clients.map((client, idx) => (
                          <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                                  {client.name.charAt(0)}
                                </div>
                                <span className="font-medium text-slate-900 dark:text-white">{client.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-medium">{formatHours(client.total_hours)}</td>
                            <td className="px-4 py-3 text-center">{client.projects_count}</td>
                            <td className="px-4 py-3 text-center">{client.employees_count}</td>
                            <td className="px-4 py-3 text-right text-slate-600">{client.entries_count}</td>
                          </tr>
                        ))}
                        {clientData.clients.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                              No client data for this period
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ATTENDANCE TAB */}
          <TabsContent value="attendance" className="space-y-4">
            {attendanceData && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">{attendanceData.summary?.avg_attendance_rate || 0}%</p>
                      <p className="text-sm text-slate-500">Avg Attendance Rate</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-600">{attendanceData.summary?.perfect_attendance || 0}</p>
                      <p className="text-sm text-slate-500">Perfect Attendance</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">{Array.isArray(attendanceData.working_days) ? attendanceData.working_days.length : attendanceData.working_days || 0}</p>
                      <p className="text-sm text-slate-500">Working Days</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Attendance Grid */}
                <Card>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full" data-testid="attendance-table">
                      <thead className="bg-slate-50 dark:bg-slate-800/50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase sticky left-0 bg-slate-50 dark:bg-slate-800/50">Employee</th>
                          {(Array.isArray(attendanceData.working_days) ? attendanceData.working_days : []).map(day => (
                            <th key={day} className="px-2 py-3 text-center text-xs font-semibold text-slate-500">
                              {format(parseISO(day), 'EEE')}<br />{format(parseISO(day), 'd')}
                            </th>
                          ))}
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {attendanceData.employees.map(emp => (
                          <tr key={emp.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="px-4 py-2 sticky left-0 bg-white dark:bg-slate-900">
                              <div className="flex items-center gap-2">
                                <Avatar className="h-7 w-7">
                                  <AvatarFallback className="text-xs bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                                    {getInitials(emp.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm font-medium">{emp.name}</span>
                              </div>
                            </td>
                            {emp.days.map((day, idx) => (
                              <td key={idx} className="px-2 py-2 text-center">
                                {day.status === 'present' && <Check className="w-5 h-5 text-emerald-500 mx-auto" />}
                                {day.status === 'leave' && <Minus className="w-5 h-5 text-amber-500 mx-auto" />}
                                {day.status === 'absent' && <X className="w-5 h-5 text-red-500 mx-auto" />}
                              </td>
                            ))}
                            <td className="px-4 py-2 text-center">
                              <Badge className={emp.attendance_rate >= 80 ? 'bg-emerald-100 text-emerald-700' : emp.attendance_rate >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                                {emp.attendance_rate}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Legend */}
                <div className="flex items-center gap-6 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span>Tracked time</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Minus className="w-4 h-4 text-amber-500" />
                    <span>On leave</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="w-4 h-4 text-red-500" />
                    <span>No tracking</span>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* OVERTIME TAB */}
          <TabsContent value="overtime" className="space-y-4">
            {overtimeData && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-900/30/50 dark:bg-red-900/5">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-red-600">{overtimeData.summary.total_employees_with_overtime}</p>
                      <p className="text-sm text-slate-500">Employees with Overtime</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">{formatHours(overtimeData.summary.total_overtime_hours)}</p>
                      <p className="text-sm text-slate-500">Total Overtime Hours</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">{overtimeData.summary.avg_overtime_percent}%</p>
                      <p className="text-sm text-slate-500">Avg Overtime %</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Overtime Table */}
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="overtime-table">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Employee</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Target Hours</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actual Hours</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Overtime</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {overtimeData.employees.map(emp => (
                            <tr key={emp.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${emp.is_overtime ? 'bg-red-50 dark:bg-red-900/30/50 dark:bg-red-900/5' : ''}`}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <Avatar className="h-9 w-9">
                                    {emp.profile_photo && <AvatarImage src={emp.profile_photo} />}
                                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm">
                                      {getInitials(emp.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <p className="font-medium text-slate-900 dark:text-white">{emp.name}</p>
                                    <p className="text-xs text-slate-500">{emp.department || <span className="italic">Not assigned</span>}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right text-slate-600">{formatHours(emp.target_hours)}</td>
                              <td className="px-4 py-3 text-right font-medium">{formatHours(emp.actual_hours)}</td>
                              <td className="px-4 py-3 text-right">
                                {emp.is_overtime ? (
                                  <span className="font-medium text-red-600">+{formatHours(emp.overtime_hours)} ({emp.overtime_percent}%)</span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {emp.is_overtime ? (
                                  <Badge className="bg-red-100 text-red-700">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    Overtime
                                  </Badge>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700">Normal</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                          {overtimeData.employees.length === 0 && (
                            <tr>
                              <td colSpan={5} className="px-4 py-12 text-center text-slate-500">
                                No employees tracked time this period
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Activity tab moved to Activity → Activity Monitor */}
        </Tabs>

        {/* Drill-Down Modal */}
        <Dialog open={drillDownModalOpen} onOpenChange={setDrillDownModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="drilldown-modal">
            <DialogHeader>
              <DialogTitle>
                {selectedEmployee ? (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      {drillDownData?.employee?.profile_photo && <AvatarImage src={drillDownData.employee.profile_photo} />}
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                        {getInitials(drillDownData?.employee?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <span>{drillDownData?.employee?.name}</span>
                      <p className="text-sm font-normal text-slate-500">{drillDownData?.employee?.department}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: drillDownData?.project?.color }} />
                    <span>{drillDownData?.project?.name}</span>
                  </div>
                )}
              </DialogTitle>
            </DialogHeader>

            {drillDownData && (
              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-xl font-bold">{formatHours(drillDownData.total_hours)}</p>
                      <p className="text-sm text-slate-500">Total Hours</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-xl font-bold">{drillDownData.daily_breakdown?.length || 0}</p>
                      <p className="text-sm text-slate-500">Days Tracked</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-xl font-bold">
                        {selectedEmployee ? drillDownData.project_breakdown?.length || 0 : drillDownData.employee_breakdown?.length || 0}
                      </p>
                      <p className="text-sm text-slate-500">{selectedEmployee ? 'Projects' : 'Team Members'}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Breakdown */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Daily Chart */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Daily Hours</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[200px]">
                        {(!drillDownData.daily_breakdown || drillDownData.daily_breakdown.length === 0) ? (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <BarChart3 className="w-10 h-10 mb-2 text-slate-300" />
                            <p className="text-sm">No data for this period</p>
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={drillDownData.daily_breakdown}>
                              <XAxis dataKey="date" tickFormatter={(d) => format(parseISO(d), 'MMM d')} fontSize={11} />
                              <YAxis fontSize={11} />
                              <Tooltip formatter={(v) => `${v.toFixed(1)}h`} labelFormatter={(d) => format(parseISO(d), 'EEEE, MMM d')} />
                              <Bar dataKey="hours" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Breakdown List */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">{selectedEmployee ? 'By Project' : 'By Employee'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {(selectedEmployee ? drillDownData.project_breakdown : drillDownData.employee_breakdown)?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <div className="flex items-center gap-2">
                              {selectedEmployee ? (
                                <>
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                                  <span className="text-sm">{item.name}</span>
                                </>
                              ) : (
                                <>
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback className="text-xs bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                                      {getInitials(item.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{item.name}</span>
                                </>
                              )}
                            </div>
                            <span className="text-sm font-medium">{formatHours(item.hours)}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Entries (for project drill-down) */}
                {selectedProject && drillDownData.recent_entries && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Recent Time Entries</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="max-h-[300px] overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Date</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Employee</th>
                              <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500">Description</th>
                              <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500">Hours</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {drillDownData.recent_entries.slice(0, 20).map((entry, idx) => (
                              <tr key={idx} className="text-sm">
                                <td className="px-4 py-2 text-slate-600">{format(parseISO(entry.start_time), 'MMM d')}</td>
                                <td className="px-4 py-2">{entry.employee?.name || 'Unknown'}</td>
                                <td className="px-4 py-2 text-slate-600 truncate max-w-[200px]">{entry.description || <span className="italic text-slate-400">No description</span>}</td>
                                <td className="px-4 py-2 text-right font-medium">{formatHours(entry.hours)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDrillDownModalOpen(false)}>Close</Button>
              <Button onClick={() => {
                if (selectedEmployee) handleExportCSV('detailed');
                else handleExportCSV('by-project');
              }}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
